import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { variable } from '~/prix/types/presets/variable'
import legalEntityStatsAction from './legalEntityStats.action.json' assert { type: 'json' }
import expression from '~/prix/types/expression'
import prefixProperties from '~/prix/utils/prefixProperties'
import cnaeEntity from '~/packages/cnae/cnae.entity'
import { pick } from '~/prix/utils/types/object'
import cnaeDivision from '~/packages/cnaeDivision/cnaeDivision.entity'

const attendancePercentageShape = object({
  hasAppSebraeAttendanceInCurrentYear: number().label('Atendimentos do App SEBRAE no ano atual'),
  totalCount: number().label('Total de empresas'),
})

const attendancePercentageEadShape = object({
  hasEadAttendanceInCurrentYear: number().label('Atendimentos do EAD no ano atual'),
})

const segmentShape = object({
  segmentId: string().label('ID do segmento'),
  segmentName: string().label('Nome do segmento'),
  segmentDescription: string().label('Descrição do segmento'),
})

const legalEntityShape = object({
  //legalEntity
  id: string().label('ID', 'Identificador', 'Código'),
  tradeName: string().label('Nome fantasia'),
  corporateName: string().isOptional().label('Razão social'),
  agentCpf: string().label('CPF do agente'),
  agentPolygonCpf: string().label('Polígono do agente'),
  cnpj: string().label('CNPJ'),
  openingDate: dateTime().label('Data de abertura'),
  email: string().label('Email'),
  street: string().label('Logradouro'),
  number: string().label('Número'),
  city: string().label('Cidade'),
  neighborhood: string().label('Bairro'),
  state: string().label('Estado'),
  registrationStatus: enumerated({
    active: 'Ativo',
    inapt: 'Inapto',
    suspended: 'Suspenso',
    extinct: 'Extinto',
  }).label('Status'),
  registrationStatusDate: dateTime().label('Data de situação cadastral'),
  registrationStatusReason: string().label('Motivo do status de registro'),
  size: enumerated({
    MEI: 'MEI',
    ME: 'ME',
    EPP: 'EPP',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Porte'),
  sizeAbbreviation: enumerated({
    MEI: 'MEI',
    ME: 'ME',
    EPP: 'EPP',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Sigla do porte'),
  ageGroup: enumerated({
    startup: 'Startup',
    young: 'Jovem',
    growth: 'Crescimento',
    established: 'Estabelecida',
  }).label('Maturidade'),
  isSimples: string().label('É Simples Nacional'),
  isMei: string().label('É MEI'),
  capital: number().label('Capital social'),
  cnaes: string().label('CNAEs'),
  legalNatureId: string().label('ID da natureza jurídica'),
  phoneNumber1AreaCode: string().label('DDD'),

  fullAddress: string().label('Endereço'),
  fullPhoneNumber1: string().label('Telefone principal'),
  fullPhoneNumber2: string().label('Telefone secundário'),

  mainCnaeId: string().label('CNAE principal'),
  mainCnaeDescription: string().label('Descrição do CNAE principal'),
  legalNatureDescription: string().label('Descrição da natureza jurídica'),

  deletedAt: string().label('Data de exclusão'),
  // geo
  pointOnStreet: object({
    type: string().label('Tipo'),
    coordinates: object({
      0: number().label('Longitude'),
      1: number().label('Latitude'),
    }),
  }).label('Ponto na rua'),

  ...prefixProperties('cnae.', cnaeEntity.schema.shape),
  ...prefixProperties('attendancePercentage.', attendancePercentageShape.shape),
  ...prefixProperties('attendancePercentageEad.', attendancePercentageEadShape.shape),
  ...prefixProperties('segment.', segmentShape.shape),
  ...prefixProperties('cnaeDivision.', cnaeDivision.schema.shape),
})
export const legalEntityExpression = expression(legalEntityShape.shape)

export const legalEntityVariable: Variable = {
  key: 'legalEntity',
  categoryKey: 'legalEntities',
  categoryKeys: ['legalEntities'],
  actionDefinitionKey: legalEntityStatsAction.key,
  title: 'Empresas',
  unitSingular: 'empresa',
  unitPlural: 'empresas',
  grammaticalGender: 'female',
  timeType: 'currentOnly',
  shape: legalEntityShape,
  betterDensity: 'more',
  basicSelect: ['corporateName', 'tradeName', 'cnpj'],
  exportSortBy: { key: 'openingDate', direction: 'desc' },
  detailedSelect: [
    'id',
    'tradeName',
    'corporateName',
    'cnpj',
    'registrationStatus',
    'registrationStatusDate',
    'openingDate',
    'mainCnaeId',
    'mainCnaeDescription',
    'segment.segmentId',
    'segment.segmentName',
    'segment.segmentDescription',
    'cnaes',
    'legalNatureId',
    'legalNatureDescription',
    'sizeAbbreviation',
    'ageGroup',
    'isSimples',
    'isMei',
    'email',
    'street',
    'number',
    'neighborhood',
    'city',
    'state',
    'phoneNumber1AreaCode',
    'fullPhoneNumber1',
  ],
  filterableBy: expression(
    pick(legalEntityExpression.shape, [
      'size',
      'ageGroup',
      'registrationStatus',
      'agentPolygonCpf',
    ]),
  ),
  assertions: prepareAssertions(legalEntityExpression),
}

const legalEntityStatsDefinition = defineAction({
  ...legalEntityStatsAction,
  input: object({
    variable,
    highlight: legalEntityExpression.isOptional(),
    filter: legalEntityExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: object({
      type: string().isOptional(),
      title: string().isOptional(),
      startDate: string().isOptional(),
      endDate: string().isOptional(),
      groupType: string().isOptional(),
    }).isOptional(),
    select: select(legalEntityShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      numerator: number().isOptional(),
      denominator: number().isOptional(),
      geoId: string(),
      geoType: childrenType.isOptional(),
    }),
  }),
  cache: input => {
    if (input.space?.delimitationType === 'custom') {
      return {}
    }
    if (input.filter) {
      return {
        back: {
          expirationMsForFallback: 1000 * 60 * 60 * 6,
          expirationMsForUpdate: 1000 * 60 * 60 * 6,
        },
        front: {
          expirationMsForFallback: 1000 * 60 * 60 * 6,
          expirationMsForUpdate: 1000 * 60 * 60 * 6,
        },
      }
    }
    return {
      back: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 6,
      },
      front: {
        expirationMsForFallback: 1000 * 60 * 60 * 24 * 7,
        expirationMsForUpdate: 1000 * 60 * 60 * 6,
      },
    }
  },
})

export default legalEntityStatsDefinition
